import React, { useState, useEffect } from 'react';
import { AdminHeader } from './AdminLayout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { 
    ShieldCheck,
    Shield,
    UserCog,
    Eye,
    User,
    RotateCcw,
    Save,
    Loader2,
    FileText,
    Users,
    Music,
    MapPin,
    Settings,
    Info
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Module icons
const MODULE_ICONS = {
    angebote: FileText,
    lehrpersonen: Users,
    instrumente: Music,
    standorte: MapPin,
    system: Settings
};

// Role config for display
const ROLE_DISPLAY = {
    super_admin: { label: 'Super Admin', icon: ShieldCheck, color: 'text-red-600', bgColor: 'bg-red-50' },
    admin: { label: 'Admin', icon: Shield, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    verwaltung: { label: 'Verwaltung', icon: UserCog, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    betrachter: { label: 'Betrachter', icon: Eye, color: 'text-gray-600', bgColor: 'bg-gray-50' },
};

export function EinstellungenRollen() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [changes, setChanges] = useState({});
    
    useEffect(() => {
        fetchPermissions();
    }, []);
    
    const fetchPermissions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const res = await axios.get(`${API_URL}/api/permissions/all-roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(res.data);
            setChanges({});
        } catch (err) {
            console.error('Failed to fetch permissions:', err);
            toast.error('Fehler beim Laden der Berechtigungen');
        } finally {
            setLoading(false);
        }
    };
    
    const handleToggle = (roleId, moduleId, actionId, currentValue) => {
        setChanges(prev => {
            const roleChanges = prev[roleId] || {};
            const moduleChanges = roleChanges[moduleId] || {};
            return {
                ...prev,
                [roleId]: {
                    ...roleChanges,
                    [moduleId]: {
                        ...moduleChanges,
                        [actionId]: !currentValue
                    }
                }
            };
        });
    };
    
    const getPermissionValue = (role, moduleId, actionId) => {
        if (changes[role.role_id]?.[moduleId]?.[actionId] !== undefined) {
            return changes[role.role_id][moduleId][actionId];
        }
        return role.permissions[moduleId]?.[actionId] ?? false;
    };
    
    const hasChanges = (roleId) => {
        return Object.keys(changes[roleId] || {}).length > 0;
    };
    
    const saveRolePermissions = async (roleId) => {
        if (!hasChanges(roleId)) return;
        
        const role = data.roles.find(r => r.role_id === roleId);
        if (!role) return;
        
        const mergedPermissions = { ...role.permissions };
        Object.entries(changes[roleId] || {}).forEach(([moduleId, actions]) => {
            mergedPermissions[moduleId] = {
                ...(mergedPermissions[moduleId] || {}),
                ...actions
            };
        });
        
        try {
            setSaving(prev => ({ ...prev, [roleId]: true }));
            const token = localStorage.getItem('auth_token');
            
            await axios.put(`${API_URL}/api/permissions/role/${roleId}`, 
                { permissions: mergedPermissions },
                { headers: { Authorization: `Bearer ${token}` }}
            );
            
            toast.success(`Berechtigungen für ${ROLE_DISPLAY[roleId]?.label || roleId} gespeichert`);
            
            setChanges(prev => {
                const updated = { ...prev };
                delete updated[roleId];
                return updated;
            });
            
            await fetchPermissions();
        } catch (err) {
            console.error('Failed to save permissions:', err);
            toast.error('Fehler beim Speichern der Berechtigungen');
        } finally {
            setSaving(prev => ({ ...prev, [roleId]: false }));
        }
    };
    
    const resetRolePermissions = async (roleId) => {
        try {
            setSaving(prev => ({ ...prev, [roleId]: true }));
            const token = localStorage.getItem('auth_token');
            
            await axios.post(`${API_URL}/api/permissions/reset/${roleId}`, {},
                { headers: { Authorization: `Bearer ${token}` }}
            );
            
            toast.success('Berechtigungen auf Standard zurückgesetzt');
            
            setChanges(prev => {
                const updated = { ...prev };
                delete updated[roleId];
                return updated;
            });
            await fetchPermissions();
        } catch (err) {
            console.error('Failed to reset permissions:', err);
            toast.error('Fehler beim Zurücksetzen');
        } finally {
            setSaving(prev => ({ ...prev, [roleId]: false }));
        }
    };
    
    if (loading) {
        return (
            <>
                <AdminHeader title="Berechtigungen" />
                <div className="admin-content">
                    <div className="max-w-5xl">
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    </div>
                </div>
            </>
        );
    }
    
    if (!data) {
        return (
            <>
                <AdminHeader title="Berechtigungen" />
                <div className="admin-content">
                    <div className="max-w-5xl">
                        <div className="text-center py-12 text-muted-foreground">
                            Keine Berechtigungsdaten gefunden
                        </div>
                    </div>
                </div>
            </>
        );
    }
    
    return (
        <>
            <AdminHeader title="Berechtigungen" />
            
            <div className="admin-content">
                <div className="max-w-5xl space-y-6">
                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex gap-3">
                        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                            <p className="font-medium">Modulare Berechtigungen</p>
                            <p className="mt-1">
                                Hier kannst du pro Modul und Aktion festlegen, welche Berechtigungen jede Rolle hat.
                                Super Admin hat immer alle Rechte und kann nicht geändert werden.
                            </p>
                        </div>
                    </div>
                    
                    <Accordion type="multiple" className="space-y-4" defaultValue={['admin']}>
                        {data.roles.map(role => {
                            const config = ROLE_DISPLAY[role.role_id] || {
                                label: role.role_label,
                                icon: Eye,
                                color: 'text-gray-600',
                                bgColor: 'bg-gray-50'
                            };
                            const RoleIcon = config.icon;
                            const canEdit = role.can_edit;
                            const roleHasChanges = hasChanges(role.role_id);
                            
                            return (
                                <AccordionItem 
                                    key={role.role_id} 
                                    value={role.role_id}
                                    className="bg-card rounded-2xl border overflow-hidden"
                                    data-testid={`role-permissions-${role.role_id}`}
                                >
                                    <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-muted/30">
                                        <div className="flex items-center gap-3">
                                            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", config.bgColor)}>
                                                <RoleIcon className={cn("h-5 w-5", config.color)} />
                                            </div>
                                            <div className="text-left">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold">{config.label}</span>
                                                    {roleHasChanges && (
                                                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                                                            Ungespeichert
                                                        </span>
                                                    )}
                                                    {!canEdit && (
                                                        <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">
                                                            Nicht editierbar
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground font-normal">
                                                    {role.is_default ? 'Standard-Berechtigungen' : 'Angepasste Berechtigungen'}
                                                </p>
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    
                                    <AccordionContent className="px-5 pb-5">
                                        <div className="space-y-4">
                                            {data.modules.map(module => {
                                                const ModuleIcon = MODULE_ICONS[module.id] || FileText;
                                                
                                                return (
                                                    <div 
                                                        key={module.id} 
                                                        className="border rounded-xl p-4"
                                                        data-testid={`module-permissions-${role.role_id}-${module.id}`}
                                                    >
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <ModuleIcon className="h-4 w-4 text-muted-foreground" />
                                                            <span className="font-medium">{module.label}</span>
                                                            <span className="text-xs text-muted-foreground ml-2">
                                                                {module.description}
                                                            </span>
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                                            {module.actions.map(action => {
                                                                const isEnabled = getPermissionValue(role, module.id, action.id);
                                                                
                                                                return (
                                                                    <div 
                                                                        key={action.id}
                                                                        className="flex items-center gap-2"
                                                                    >
                                                                        <Switch
                                                                            id={`${role.role_id}-${module.id}-${action.id}`}
                                                                            checked={isEnabled}
                                                                            disabled={!canEdit}
                                                                            onCheckedChange={() => 
                                                                                canEdit && handleToggle(role.role_id, module.id, action.id, isEnabled)
                                                                            }
                                                                            data-testid={`switch-${role.role_id}-${module.id}-${action.id}`}
                                                                        />
                                                                        <label 
                                                                            htmlFor={`${role.role_id}-${module.id}-${action.id}`}
                                                                            className={cn(
                                                                                "text-sm cursor-pointer select-none",
                                                                                !canEdit && "opacity-50 cursor-not-allowed"
                                                                            )}
                                                                        >
                                                                            {action.label}
                                                                        </label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            
                                            {canEdit && (
                                                <div className="flex items-center gap-2 pt-2 border-t">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => saveRolePermissions(role.role_id)}
                                                        disabled={!roleHasChanges || saving[role.role_id]}
                                                        className="rounded-lg"
                                                        data-testid={`save-permissions-${role.role_id}`}
                                                    >
                                                        {saving[role.role_id] ? (
                                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                        ) : (
                                                            <Save className="h-4 w-4 mr-2" />
                                                        )}
                                                        Speichern
                                                    </Button>
                                                    
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => resetRolePermissions(role.role_id)}
                                                        disabled={role.is_default || saving[role.role_id]}
                                                        className="rounded-lg"
                                                        data-testid={`reset-permissions-${role.role_id}`}
                                                    >
                                                        <RotateCcw className="h-4 w-4 mr-2" />
                                                        Auf Standard zurücksetzen
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            );
                        })}
                    </Accordion>
                </div>
            </div>
        </>
    );
}
